// The Firebase implementation of AuthClient (Frontend §5.1): `firebase/auth` only, email + password, no popup
// resolver (so CSP needs no frame-src for firebaseapp.com). The emulator is used when its host is configured.
import { getApps, initializeApp } from 'firebase/app'
import {
  browserLocalPersistence,
  confirmPasswordReset,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  initializeAuth,
  onIdTokenChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  verifyPasswordResetCode,
  type Auth,
} from 'firebase/auth'
import { AuthError, type AuthClient, type AuthUser, type FirebaseWebConfig } from './client'

function wrap(e: unknown): unknown {
  const code = (e as { code?: unknown } | null)?.code
  return typeof code === 'string' ? new AuthError(code) : e
}

async function call<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn()
  } catch (e) {
    throw wrap(e)
  }
}

export function createFirebaseAuthClient(config: FirebaseWebConfig): AuthClient {
  const name = 'pa-staff'
  const app =
    getApps().find((a) => a.name === name) ??
    initializeApp(
      { apiKey: config.apiKey, authDomain: config.authDomain, projectId: config.projectId, appId: config.appId },
      name,
    )
  const auth: Auth = initializeAuth(app, { persistence: [indexedDBLocalPersistence, browserLocalPersistence] })
  if (config.emulatorHost) connectAuthEmulator(auth, `http://${config.emulatorHost}`, { disableWarnings: true })

  const toUser = (u: { uid: string; email: string | null } | null): AuthUser | null =>
    u ? { uid: u.uid, email: u.email ?? '' } : null

  return {
    onChange(listener) {
      return onIdTokenChanged(auth, (u) => {
        listener(toUser(u))
      })
    },
    signIn: (email, password) =>
      call(async () => {
        await signInWithEmailAndPassword(auth, email, password)
      }),
    signOut: () => call(() => signOut(auth)),
    changePassword: (newPassword) =>
      call(async () => {
        const user = auth.currentUser
        if (!user) throw new AuthError('auth/no-current-user')
        await updatePassword(user, newPassword)
        // The change moves tokensValidAfterTime, and the API's verifyIdToken(checkRevoked) compares it with the
        // token's auth_time, which a plain refresh keeps. So sign in again with the new password, once the next whole
        // second has started (revocation has 1-second granularity): the fresh token's auth_time is after the change,
        // which is also what POST /v1/me/password-changed checks (Backend §14.3).
        await new Promise((resolve) => setTimeout(resolve, 1_050 - (Date.now() % 1_000)))
        if (user.email) await signInWithEmailAndPassword(auth, user.email, newPassword)
        else await user.getIdToken(true)
      }),
    sendPasswordReset: (email, continueUrl) => call(() => sendPasswordResetEmail(auth, email, { url: continueUrl })),
    verifyPasswordResetCode: (oobCode) => call(() => verifyPasswordResetCode(auth, oobCode)),
    confirmPasswordReset: (oobCode, newPassword) => call(() => confirmPasswordReset(auth, oobCode, newPassword)),
    getIdToken: (forceRefresh) =>
      call(async () => (auth.currentUser ? auth.currentUser.getIdToken(forceRefresh) : null)),
  }
}
